import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Flex,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  IconButton,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';

import { 
  FiSearch, 
  FiRefreshCw, 
  FiShare2,
  FiLayers,
  FiCheckCircle,
  FiTool,
  FiXCircle,
  FiAlertTriangle,
  FiImage
} from 'react-icons/fi';
import PageHeader from './shared/PageHeader';
import html2canvas from 'html2canvas';
import { AiFillFilePdf } from 'react-icons/ai';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAssetRecords } from '../utils/firebaseUtils';
import { getOptimizedImageUrl } from '../utils/misc';
import type { AssetRecord, AssetSearchFilters } from '../types/asset';
import { ASSET_CATEGORIES, ASSET_STATUSES } from '../types/asset';
import ImageViewerModal from './shared/ImageViewerModal';
import CustomDropdown from './subComponents/DropDown'
import type { DropdownOption } from './subComponents/DropDown';

// type CategoryOption = 'ALL' | typeof ASSET_CATEGORIES[number];

// Asset category options with emoji icons
const assetCategoryOptions: DropdownOption[] = [
  { id: 'ALL', label: 'All Categories', iconSrc: '📋', subtitle: undefined },
  ...ASSET_CATEGORIES.map((cat) => {
    let icon = '📦';
    switch (cat) {
      case 'Vehicles': icon = '🚗'; break;
      case 'Office Equipment': icon = '🖨️'; break;
      case 'Technology': icon = '💻'; break;
      case 'Furniture': icon = '🪑'; break;
      case 'Tools': icon = '🛠️'; break;
      case 'Safety Equipment': icon = '🦺'; break;
      case 'Other': icon = '📦'; break;
    }
    return { id: cat, label: cat, iconSrc: icon, subtitle: undefined };
  })
];

// Asset status options with emoji icons
const assetStatusOptions: DropdownOption[] = [
  { id: 'ALL', label: 'All Statuses', iconSrc: '📋' },
  ...ASSET_STATUSES.map((status) => {
    let icon = '🔵';
    const value = (status as any).value || status;
    const label = (status as any).label || value;
    switch (String(value).toLowerCase()) {
      case 'active': icon = '✅'; break;
      case 'maintenance': icon = '🛠️'; break;
      case 'retired': icon = '📦'; break;
      case 'stolen': icon = '🚨'; break;
      case 'lost-stolen': icon = '🚨'; break;
      default: icon = '🔵'; break;
    }
    return { id: value, label, iconSrc: icon };
  })
];

// Status configuration with icons and colors
const STATUS_CONFIG = {
  'totalAssets': {
    icon: FiLayers,
    label: 'Total Assets',
    color: 'blue',
    bgGradient: 'linear(to-r, blue.400, blue.600)'
  },
  'active': {
    icon: FiCheckCircle,
    label: 'Active',
    color: 'green',
    bgGradient: 'linear(to-r, green.400, green.600)'
  },
  'maintenance': {
    icon: FiTool,
    label: 'Maintenance',
    color: 'orange',
    bgGradient: 'linear(to-r, orange.400, orange.600)'
  },
  'retired': {
    icon: FiXCircle,
    label: 'Retired',
    color: 'gray',
    bgGradient: 'linear(to-r, gray.400, gray.600)'
  },
  'stolen': {
    icon: FiAlertTriangle,
    label: 'Stolen',
    color: 'red',
    bgGradient: 'linear(to-r, red.400, red.600)'
  }
};

const AssetManagement: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCompany } = useAuth();
  const toast = useToast();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // State
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalAssetValue, setTotalAssetValue] = useState(0);

  // For status card modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalKey, setStatusModalKey] = useState<string | null>(null);
  const [statusModalAssets, setStatusModalAssets] = useState<AssetRecord[]>([]);
  const [statusModalLoading, setStatusModalLoading] = useState(false);
  // Sum of all asset values
  const [statusCounts, setStatusCounts] = useState({
    totalValue: 0,
    totalAssets: 0,
    active: 0,
    maintenance: 0,
    retired: 0,
    stolen: 0
  });

  // Search filters
  const [filters, setFilters] = useState<AssetSearchFilters>({
    assetName: '',
    category: 'ALL',
    status: 'ALL',
    assignedTo: ''
  });

  // Calculate status counts only
  const calculateStatusCounts = (assetList: AssetRecord[]) => {
    const counts = {
      totalValue: 0,
      totalAssets: assetList.length,
      active: 0,
      maintenance: 0,
      retired: 0,
      stolen: 0
    };
    
    assetList.forEach(asset => {
      const status = asset.status?.toLowerCase();
      if (status) {
        // Handle different status mappings
        if (status === 'active') {
          counts.active++;
        } else if (status === 'maintenance') {
          counts.maintenance++;
        } else if (status === 'retired') {
          counts.retired++;
        } else if (status === 'stolen' || status === 'lost-stolen') {
          counts.stolen++;
        }
      }
      console.log("asset.status:", asset.status, "mapped to:", status);
      counts.totalValue += Number(asset.value || 0);
    });
    console.log('Final counts:', counts);
    setTotalAssetValue(counts.totalValue);
    setStatusCounts(counts);
  };

  // Load initial asset counts on component mount
  useEffect(() => {
    const loadInitialCounts = async () => {
      try {
        const allAssets = await getAssetRecords({
          company: selectedCompany?.companyName
        });
        calculateStatusCounts(allAssets);
      } catch (error) {
        console.error('Error loading initial asset counts:', error);
      }
    };

    if (selectedCompany?.companyName) {
      loadInitialCounts();
    }
  }, [selectedCompany?.companyName]);

  // Handle search
  const handleSearch = async () => {
    setSearching(true);
    setLoading(true);

    try {
      const searchFilters: AssetSearchFilters = {
        assignedTo: filters.assignedTo,
        category: filters.category === 'ALL' ? '' : filters.category,
        status: filters.status === 'ALL' ? '' : filters.status,
        company: selectedCompany?.companyName
      };

      const results = await getAssetRecords(searchFilters);
      setAssets(results);
      setHasSearched(true);
      calculateStatusCounts(results);

      if (results.length === 0) {
        toast({
          title: 'No Results',
          description: 'No asset records found for the specified criteria',
          status: 'info',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error searching assets:', error);
      toast({
        title: 'Search Error',
        description: 'Failed to search asset records. Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  // Handle status card click (opens modal and searches by company + status only)
  const handleStatusClick = async (status: string) => {
    setStatusModalKey(status);
    setStatusModalOpen(true);
    setStatusModalLoading(true);
    try {
      let statusFilter = '';
      
      // Map UI status keys to actual database status values
      if (status === 'totalAssets') {
        statusFilter = ''; // Get all assets
      } else if (status === 'stolen') {
        // Handle both 'stolen' and 'lost-stolen' statuses
        statusFilter = ''; // We'll need to get all and filter in the results
      } else {
        statusFilter = status;
      }
      
      const searchFilters: AssetSearchFilters = {
        assetName: '',
        category: '',
        status: statusFilter,
        company: selectedCompany?.companyName
      };
      
      const allResults = await getAssetRecords(searchFilters);
      
      // Additional filtering for stolen status to include both 'stolen' and 'lost-stolen'
      let filteredResults = allResults;
      if (status === 'stolen') {
        filteredResults = allResults.filter(asset => 
          asset.status?.toLowerCase() === 'stolen' || 
          asset.status?.toLowerCase() === 'lost-stolen'
        );
      } else if (status !== 'totalAssets') {
        filteredResults = allResults.filter(asset => 
          asset.status?.toLowerCase() === status.toLowerCase()
        );
      }
      
      setStatusModalAssets(filteredResults);
    } catch (error) {
      setStatusModalAssets([]);
      toast({
        title: 'Search Error',
        description: 'Failed to search asset records. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setStatusModalLoading(false);
    }
  };

  // Format date for display
  const formatDateTime = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date only
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Reusable PDF export function
  // Parameters:
  // - assetData: Array of asset records to export
  // - reportTitle: Main title for the PDF report
  // - subtitle: Subtitle/description for the report
  // - filename: Base filename (timestamp will be added automatically)
  // - includeStatus: Whether to include the status column (true for status modal, false for search results)
  const exportAssetsToPDF = async (
    assetData: AssetRecord[], 
    reportTitle: string, 
    subtitle: string, 
    filename: string,
    includeStatus: boolean = false
  ) => {
    try {
      // Dynamically import jsPDF
      const jsPDF = (await import('jspdf')).default;
      
      // Set PDF to landscape orientation and A3 size
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3'});
      const autoTable = (await import('jspdf-autotable')).default;
      
      // Add title and subtitle
      doc.setFontSize(12);
      doc.text(reportTitle, 10, 20);
      doc.setFontSize(10);
      doc.text(subtitle, 10, 30);
      
      // Define table columns based on whether to include status
      const columns = includeStatus ? [
        { header: 'Asset Code', dataKey: 'assetCode' },
        { header: 'Asset Name', dataKey: 'assetName' },
        { header: 'Description', dataKey: 'description' },
        { header: 'Location', dataKey: 'location' },
        { header: 'Category', dataKey: 'category' },
        { header: 'Status', dataKey: 'status' },
        { header: 'Assigned To', dataKey: 'assignedTo' },
        { header: 'Purchase Date', dataKey: 'purchaseDate' },
        { header: 'Value', dataKey: 'value' },
        { header: 'Image', dataKey: 'image' },
      ] : [
        { header: 'Asset Code', dataKey: 'assetCode' },
        { header: 'Asset Name', dataKey: 'assetName' },
        { header: 'Description', dataKey: 'description' },
        { header: 'Location', dataKey: 'location' },
        { header: 'Category', dataKey: 'category' },
        { header: 'Assigned To', dataKey: 'assignedTo' },
        { header: 'Purchase Date', dataKey: 'purchaseDate' },
        { header: 'Value', dataKey: 'value' },
        { header: 'Has Image?', dataKey: 'image' },
      ];
      
      // Process asset data for table rows
      const rows: Record<string, string | number>[] = assetData.map(d => {
        const baseRow = {
          assetCode: d.assetCode,
          assetName: d.assetName,
          description: d.description ?? '',
          location: d.location ?? '',
          category: d.category,
          assignedTo: d.assignedTo ?? '',
          purchaseDate: d.purchaseDate ? formatDateTime(d.purchaseDate) : '',
          value: d.value ?? '',
          image: d.image && d.image.trim() !== '' ? 'Yes' : 'No',
        };
        
        if (includeStatus) {
          return { ...baseRow, status: d.status };
        }
        return baseRow;
      });
      
      // Define column styles based on whether status is included
      const columnStyles: { [key: string]: { cellWidth: number } } = includeStatus ? {
        '0': { cellWidth: 30 },   // Asset Code
        '1': { cellWidth: 35 },   // Asset Name
        '2': { cellWidth: 40 },   // Description
        '3': { cellWidth: 25 },   // Location
        '4': { cellWidth: 30 },   // Category
        '5': { cellWidth: 20 },   // Status
        '6': { cellWidth: 30 },   // Assigned To
        '7': { cellWidth: 30 },   // Purchase Date
        '8': { cellWidth: 25 },   // Value
        '9': { cellWidth: 20 },   // Image
      } : {
        '0': { cellWidth: 35 },   // Asset Code
        '1': { cellWidth: 40 },   // Asset Name
        '2': { cellWidth: 50 },   // Description
        '3': { cellWidth: 30 },   // Location
        '4': { cellWidth: 45 },   // Category
        '5': { cellWidth: 35 },   // Assigned To
        '6': { cellWidth: 35 },   // Purchase Date
        '7': { cellWidth: 30 },   // Value
        '8': { cellWidth: 25 },   // Image
      };
      
      // Create the table
      autoTable(doc, {
        startY: 40,
        head: [columns.map(col => col.header)],
        body: rows.map(row => columns.map(col => row[col.dataKey] ?? '')),
        styles: { fontSize: 9, overflow: 'linebreak' },
        headStyles: { fillColor: [41, 128, 185] },
        margin: { left: 10, right: 10 },
        theme: 'grid',
        tableWidth: 'auto',
        columnStyles
      });
      
      // Generate timestamp for filename
      const today = new Date();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const yyyy = today.getFullYear();
      const timestampedFilename = `${filename}_${mm}${dd}${yyyy}.pdf`;
      
      // Generate PDF and handle sharing/download
      const pdfBlob = doc.output('blob');
      const pdfFile = new File([pdfBlob], timestampedFilename, { type: 'application/pdf' });
      
      if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        await navigator.share({
          files: [pdfFile],
          title: reportTitle,
          text: 'Shared from Mobile Reporting App',
        });
      } else {
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = timestampedFilename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('PDF export error:', err);
      if (window && window.alert) window.alert('Failed to export PDF');
    }
  };

  // Reset search
  const handleReset = () => {
    setFilters({
      assetName: '',
      category: 'ALL',
      status: 'ALL',
      assignedTo: ''
    });
    setAssets([]);
    setHasSearched(false);
  };

  // Status Overview Card Component
  const StatusCard: React.FC<{ 
    statusKey: string, 
    count: number, 
    isActive?: boolean 
  }> = ({ statusKey, count, isActive }) => {
    const config = STATUS_CONFIG[statusKey as keyof typeof STATUS_CONFIG];
    const IconComponent = config.icon;

    return (
      <Box
        cursor="pointer"
        onClick={() => handleStatusClick(statusKey)}
        transform={isActive ? "scale(1.02)" : "scale(1)"}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{
          transform: "translateY(-4px)",
          boxShadow: `0 12px 48px rgba(${config.color === 'blue' ? '59, 130, 246' : config.color === 'green' ? '34, 197, 94' : config.color === 'orange' ? '251, 146, 60' : config.color === 'red' ? '239, 68, 68' : '107, 114, 128'}, 0.3)`
        }}
        _active={{
          transform: "translateY(-2px)"
        }}
        bg="rgba(255, 255, 255, 0.9)"
        backdropFilter="blur(10px)"
        borderRadius="xl"
        p={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.08)"
        border="2px solid"
        borderColor={isActive ? `${config.color}.300` : "whiteAlpha.300"}
        minW={{ base: '40vw', md: '160px' }}
        maxW={{ base: '48vw', md: '220px' }}
        width="100%"
      >
        <VStack spacing={4}>
          <Box
            p={3}
            borderRadius="full"
            bgGradient={`linear(135deg, ${config.color}.400, ${config.color}.600)`}
            color="white"
            boxShadow={`0 4px 16px rgba(${config.color === 'blue' ? '59, 130, 246' : config.color === 'green' ? '34, 197, 94' : config.color === 'orange' ? '251, 146, 60' : config.color === 'red' ? '239, 68, 68' : '107, 114, 128'}, 0.4)`}
          >
            <IconComponent size="24px" />
          </Box>
          <Stat textAlign="center">
            <StatNumber 
              fontSize="lg" 
              fontWeight="800" 
              bgGradient={`linear(135deg, ${config.color}.600, ${config.color}.800)`}
              bgClip="text"
            >
              {count}
            </StatNumber>
            <StatLabel fontSize="xs" fontWeight="600" color="gray.600" noOfLines={2}>
              {config.label}
            </StatLabel>
          </Stat>
        </VStack>
      </Box>
    );
  };

  // Image modal state - now using ImageViewerModal with better state management
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);
  const [imageModalTitle, setImageModalTitle] = useState<string>('Asset Image');

  // Enhanced image modal handlers with proper cleanup and scroll preservation
  const openImageModal = (url: string, assetName: string, assetCode: string) => {
    const optimizedUrl = getOptimizedImageUrl(url, 1200, 85); // Higher quality for modal viewing
    setImageModalUrl(optimizedUrl);
    setImageModalTitle(`${assetName} (${assetCode})`);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    // Reset state after modal animation completes
    // Don't use setTimeout here as it might interfere with scroll restoration
    setImageModalUrl(null);
    setImageModalTitle('Asset Image');
  };

  // Mobile Card View for assets
  const AssetCard: React.FC<{ asset: AssetRecord }> = ({ asset }) => {
    const cardRef = React.useRef<HTMLDivElement>(null);

    const handleShare = async () => {
      if (!cardRef.current) return;
      try {
        const canvas = await html2canvas(cardRef.current, { 
          useCORS: true, 
          logging: false, 
          scale: 2 
        });
        const blob = await new Promise<Blob | null>(resolve => 
          canvas.toBlob(resolve, 'image/png', 0.9)
        );
        if (!blob) throw new Error('Failed to capture screenshot');
        
        if (navigator.canShare && navigator.canShare({ 
          files: [new File([blob], 'asset.png', { type: 'image/png' })] 
        })) {
          await navigator.share({
            files: [new File([blob], 'asset.png', { type: 'image/png' })],
            title: `Asset: ${asset.assetName}`,
            text: 'Shared from Mobile Reporting App',
          });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `asset-${asset.assetCode}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast({ 
            title: 'Screenshot downloaded', 
            status: 'success', 
            duration: 3000 
          });
        }
      } catch (err) {
        toast({ 
          title: 'Failed to capture screenshot', 
          status: 'error', 
          duration: 3000 
        });
      }
    };

    // Image modal handler with optimization
    const handleImageClick = (url: string, assetName: string, assetCode: string) => {
      openImageModal(url, assetName, assetCode);
    };

    return (
      <>
        <Card 
          key={asset.id} 
          mb={4} 
          ref={cardRef} 
          position="relative"
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          shadow="sm"
          _hover={{ shadow: "md" }}
        >
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Box position="absolute" top={-2} right={2} zIndex={10}>
                <IconButton
                  aria-label="Share this asset"
                  icon={<FiShare2 />}
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  onClick={handleShare}
                />
              </Box>
              <Box textAlign="left">
                <VStack align="stretch" spacing={2}>
                  {/* Status (text with emoji) - FIRST FIELD */}
                  <Flex wrap="wrap" gap={2} align="center">
                    <Text 
                      fontSize="md" 
                      fontWeight="bold" 
                      color={
                        asset.status === 'active' ? 'green.600' :
                        asset.status === 'maintenance' ? 'orange.600' :
                        asset.status === 'retired' ? 'gray.600' :
                        asset.status === 'stolen' || asset.status === 'lost-stolen' ? 'red.600' : 'blue.600'
                      }
                    >
                      {asset.status === 'active' ? '✅ ACTIVE' :
                       asset.status === 'maintenance' ? '🛠️ MAINTENANCE' :
                       asset.status === 'retired' ? '📦 RETIRED' :
                       asset.status === 'stolen' || asset.status === 'lost-stolen' ? '🚨 STOLEN' : 
                       `🔵 ${asset.status?.toUpperCase()}`}
                    </Text>
                  </Flex>
                  {/* Asset Code */}
                  <Text>
                    <Box as="span" fontWeight="bold" color="gray.700">Code:</Box>
                    <Box as="span" color="blue.600" fontWeight="semibold" ml={2}>{asset.assetCode}</Box>
                  </Text>
                  {/* Asset Name */}
                  <Text>
                    <Box as="span" fontWeight="bold" color="gray.700">Name:</Box>
                    <Box as="span" color="blue.600" fontWeight="medium" ml={2}>{asset.assetName}</Box>
                  </Text>
                  {/* Description */}
                  {asset.description && (
                    <Text>
                      <Box as="span" fontWeight="bold" color="gray.700">Description:</Box>
                      <Box as="span" color="gray.600" ml={2}>{asset.description}</Box>
                    </Text>
                  )}
                  {/* Location */}
                  {asset.location && (
                    <Text>
                      <Box as="span" fontWeight="bold" color="gray.700">Location:</Box>
                      <Box as="span" color="gray.600" ml={2}>{asset.location}</Box>
                    </Text>
                  )}
                  {/* Category */}
                  <Text>
                    <Box as="span" fontWeight="bold" color="gray.700">Category:</Box>
                    <Box as="span" color="gray.600" ml={2}>{asset.category}</Box>
                  </Text>
                  {/* Assigned To */}
                  {asset.assignedTo && (
                    <Text>
                      <Box as="span" fontWeight="bold" color="gray.700">Assigned To:</Box>
                      <Box as="span" color="gray.600" ml={2}>{asset.assignedTo}</Box>
                    </Text>
                  )}
                  {/* Purchase Date */}
                  {asset.purchaseDate && (
                    <Text>
                      <Box as="span" fontWeight="bold" color="gray.700">Purchase Date:</Box>
                      <Box as="span" color="gray.600" ml={2}>{formatDate(asset.purchaseDate)}</Box>
                    </Text>
                  )}
                  {/* Value */}
                  <Text>
                    <Box as="span" fontWeight="bold" color="gray.700">Value:</Box>
                    <Box as="span" color="gray.600" ml={2}>
                      {typeof asset.value === 'number' || (typeof asset.value === 'string' && asset.value !== undefined)
                        ? `₱${Number(asset.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                        : 'N/A'}
                    </Box>
                  </Text>
                  {/* Image Field */}
                  {asset.image && (
                    <Text>
                      <Box as="span" fontWeight="bold" color="gray.700">Image:</Box>
                      <Box as="span" ml={2} display="inline-flex" alignItems="center">
                        <IconButton
                          aria-label="View asset image"
                          icon={<FiImage />}
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleImageClick(asset.image as string, asset.assetName, asset.assetCode)}
                          mr={1}
                        />
                        <Box as="span" fontSize="sm" color="gray.500" cursor="pointer" onClick={() => handleImageClick(asset.image as string, asset.assetName, asset.assetCode)}>
                          click to show image
                        </Box>
                      </Box>
                    </Text>
                  )}
                  {/* Divider before Updated By */}
                  <Divider my={2} borderColor="gray.300" />
                  <Box bg="gray.50" borderRadius="md" p={2} mt={1} mb={2} border="1px" borderColor="gray.200">
                    <Text fontSize="0.8em" color="gray.500" fontStyle="italic" mb={1}>
                      <Box as="span" fontWeight="bold" color="gray.700">Updated At: </Box>
                      <Box as="span" color="blue.500" fontWeight="normal">{asset.updated_at ? formatDateTime(asset.updated_at) : 'N/A'}</Box>
                    </Text>
                    <Text fontSize="0.8em" color="gray.500" fontStyle="italic">
                      <Box as="span" fontWeight="bold" color="gray.700">Updated By: </Box>
                      <Box as="span" color="blue.500" fontWeight="normal">{asset.updated_by ? asset.updated_by : 'N/A'}</Box>
                    </Text>
                  </Box>
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
        {/* Image Modal - Using ImageViewerModal component */}
      </>
    );
  };

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)">
      <PageHeader
        title="Asset Monitoring"
        subtitle="Track and manage company assets"
        icon="🏢"
        onBack={() => navigate('/dashboard')}
      />

      {/* Main Content */}
      <Container maxW="6xl" py={12} px={4}>
        <VStack spacing={10} align="stretch">
          {/* Status Overview Cards - Accordion */}
          <Accordion 
            allowToggle 
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
          >
            <AccordionItem border="none">
              <AccordionButton
                p={{ base: 6, md: 8 }}
                _hover={{ bg: "transparent" }}
                borderRadius="2xl"
              >
                <HStack spacing={4} flex="1">
                  <Box
                    p={3}
                    borderRadius="xl"
                    bgGradient="linear(135deg, blue.400, purple.500)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="0 6px 24px rgba(59, 130, 246, 0.3)"
                  >
                    <Text fontSize="2xl">📊</Text>
                  </Box>
                  <VStack align="start" spacing={1} flex="1">
                    <Text 
                      fontSize="xl" 
                      fontWeight="700"
                      bgGradient="linear(135deg, gray.800, gray.600)"
                      bgClip="text"
                      textAlign="left"
                    >
                      Asset Status Overview
                    </Text>
                    <Text fontSize="sm" color="gray.600" fontWeight="500" textAlign="left">
                      Real-time asset statistics and monitoring
                    </Text>
                  </VStack>
                  <AccordionIcon color="gray.600" />
                </HStack>
              </AccordionButton>
              
              <AccordionPanel p={{ base: 6, md: 8 }} pt={0}>
                <SimpleGrid columns={{ base: 2, md: 6 }} spacing={6} w="100%">
                  {/* Total Asset Value Card (not clickable) - now first */}
                  <Box
                    bg="rgba(255, 255, 255, 0.9)"
                    backdropFilter="blur(10px)"
                    borderRadius="xl"
                    p={6}
                    boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                    border="2px solid"
                    borderColor="yellow.200"
                    minW={{ base: '40vw', md: '160px' }}
                    maxW={{ base: '48vw', md: '220px' }}
                    width="100%"
                    cursor="default"
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 32px rgba(251, 146, 60, 0.2)"
                    }}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  >
                    <VStack spacing={4}>
                      <Box
                        p={3}
                        borderRadius="full"
                        bgGradient="linear(135deg, yellow.400, orange.500)"
                        color="white"
                        boxShadow="0 4px 16px rgba(251, 146, 60, 0.4)"
                      >
                        <FiLayers size="24px" />
                      </Box>
                      <Stat textAlign="center">
                        <StatNumber 
                          fontSize="lg" 
                          fontWeight="800" 
                          bgGradient="linear(135deg, yellow.600, orange.600)"
                          bgClip="text"
                        >
                          ₱{totalAssetValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </StatNumber>
                        <StatLabel fontSize="xs" fontWeight="600" color="gray.600" noOfLines={2}>
                          Total Asset Value
                        </StatLabel>
                      </Stat>
                    </VStack>
                  </Box>
                  {/* Status Cards */}
                  {Object.entries(STATUS_CONFIG).map(([key]) => (
                    <StatusCard
                      key={key}
                      statusKey={key}
                      count={statusCounts[key as keyof typeof statusCounts]}
                      isActive={false}
                    />
                  ))}
                </SimpleGrid>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
      {/* Status Modal for status card click */}
      <Modal isOpen={statusModalOpen} onClose={() => setStatusModalOpen(false)} size="xl" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent 
          bg="rgba(255, 255, 255, 0.95)" 
          backdropFilter="blur(20px)"
          borderRadius="2xl"
          border="1px solid"
          borderColor="whiteAlpha.300"
        >
          <ModalHeader
            bgGradient="linear(135deg, blue.600, purple.600)"
            bgClip="text"
            fontWeight="800"
          >
            {statusModalKey && (statusModalKey in STATUS_CONFIG)
              ? STATUS_CONFIG[statusModalKey as keyof typeof STATUS_CONFIG].label
              : 'Assets'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {statusModalLoading ? (
              <Flex justify="center" py={8}>
                <VStack>
                  <Spinner size="lg" color="blue.500" />
                  <Text color="gray.600" fontWeight="500">Loading assets...</Text>
                </VStack>
              </Flex>
            ) : statusModalAssets.length === 0 ? (
              <Alert 
                status="info" 
                borderRadius="xl"
                bg="blue.50"
                border="1px solid"
                borderColor="blue.200"
              >
                <AlertIcon />
                <Text fontWeight="500">No asset records found for this status.</Text>
              </Alert>
            ) : (
              <Box>
                <Flex justify="space-between" align="center" mb={6}>
                  <HStack spacing={3}>
                    <Box
                      p={2}
                      borderRadius="lg"
                      bgGradient="linear(135deg, green.400, green.600)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="sm" color="white">✅</Text>
                    </Box>
                    <Text 
                      fontSize="md" 
                      fontWeight="700"
                      bgGradient="linear(135deg, green.600, green.800)"
                      bgClip="text"
                    >
                      Search Results ({statusModalAssets.length} records)
                    </Text>
                  </HStack>
                  <IconButton
                    aria-label="Export Status Results as PDF"
                    icon={<AiFillFilePdf />}
                    size="md"
                    variant="ghost"
                    colorScheme="red"
                    borderRadius="xl"
                    onClick={async () => {
                      const statusLabel = statusModalKey && (statusModalKey in STATUS_CONFIG) 
                        ? STATUS_CONFIG[statusModalKey as keyof typeof STATUS_CONFIG].label 
                        : 'Assets';
                      await exportAssetsToPDF(
                        statusModalAssets,
                        'Asset Status Report',
                        `${statusLabel} - ${statusModalAssets.length} records`,
                        `AssetStatus_${statusLabel.replace(/\s+/g, '_')}`,
                        true // include status column
                      );
                    }}
                  />
                </Flex>
                <VStack spacing={4} align="stretch">
                  {statusModalAssets.map(asset => (
                    <AssetCard key={asset.id} asset={asset} />
                  ))}
                </VStack>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

          {/* Search Filters */}
          <Box
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
          >
            <VStack spacing={6} align="stretch">
              <VStack spacing={4} textAlign="center">
                <Box
                  p={3}
                  borderRadius="xl"
                  bgGradient="linear(135deg, purple.400, pink.500)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 6px 24px rgba(168, 85, 247, 0.3)"
                >
                  <Text fontSize="2xl">🔍</Text>
                </Box>
                <VStack spacing={2}>
                  <Text 
                    fontSize="xl" 
                    fontWeight="700"
                    bgGradient="linear(135deg, gray.800, gray.600)"
                    bgClip="text"
                  >
                    Search Assets
                  </Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="500">
                    Filter assets by category, status, and assignment
                  </Text>
                </VStack>
              </VStack>

              {/* Search Fields */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <FormControl>
                  <FormLabel 
                    fontSize="sm" 
                    fontWeight="700" 
                    color="gray.700"
                    mb={3}
                  >
                    📦 Category
                  </FormLabel>
                  <CustomDropdown
                    options={assetCategoryOptions}
                    selectedId={filters.category ?? 'ALL'}
                    onSelectionChange={(id) => setFilters(prev => ({
                      ...prev,
                      category: id
                    }))}
                    placeholder="Select Category"
                    showIcons={true}
                    showBadgeForSingle={true}
                    maxWidth={{ base: "100%", md: "220px" }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel 
                    fontSize="sm" 
                    fontWeight="700" 
                    color="gray.700"
                    mb={3}
                  >
                    📊 Status
                  </FormLabel>
                  <CustomDropdown
                    options={assetStatusOptions}
                    selectedId={filters.status ?? 'ALL'}
                    onSelectionChange={(id) => setFilters(prev => ({
                      ...prev,
                      status: id
                    }))}
                    placeholder="Select Status"
                    showIcons={true}
                    showBadgeForSingle={true}
                    maxWidth={{ base: "100%", md: "220px" }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel 
                    fontSize="sm" 
                    fontWeight="700" 
                    color="gray.700"
                    mb={3}
                  >
                    👤 Assigned To
                  </FormLabel>
                  <Input
                    placeholder="Search by assigned user..."
                    value={filters.assignedTo}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      assignedTo: e.target.value
                    }))}
                    bg="white"
                    borderRadius="xl"
                    border="2px solid"
                    borderColor="gray.200"
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
                    }}
                    _hover={{
                      borderColor: "gray.300"
                    }}
                    fontWeight="500"
                  />
                </FormControl>
              </SimpleGrid>

              {/* Action Buttons */}
              <Flex gap={4} justify="center" wrap="nowrap" pt={2}>
                <Button
                  flex="1"
                  minW="0" // ✅ allows shrinking below content width
                  leftIcon={<FiSearch />}
                  bgGradient="linear(135deg, blue.400, blue.600)"
                  color="white"
                  onClick={handleSearch}
                  isLoading={searching}
                  loadingText="Searching..."
                  size="lg"
                  borderRadius="xl"
                  boxShadow="0 4px 20px rgba(59, 130, 246, 0.4)"
                  _hover={{
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 24px rgba(59, 130, 246, 0.5)"
                  }}
                  _active={{ transform: "translateY(0px)" }}
                  transition="all 0.2s"
                  fontWeight="600"
                  px={4} // ✅ smaller padding so it fits on mobile
                >
                  Search
                </Button>

                <Button
                  flex="1"
                  minW="0" // ✅ allows shrinking
                  leftIcon={<FiRefreshCw />}
                  variant="outline"
                  colorScheme="gray"
                  onClick={handleReset}
                  size="lg"
                  borderRadius="xl"
                  borderWidth="2px"
                  fontWeight="600"
                  _hover={{
                    bg: "gray.50",
                    transform: "translateY(-1px)"
                  }}
                  transition="all 0.2s"
                  px={4} // ✅ match Search button padding
                >
                  Reset
                </Button>
              </Flex>

            </VStack>
          </Box>

          {/* Results */}
          {loading && (
            <Box
              bg="rgba(255, 255, 255, 0.8)"
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              p={12}
              boxShadow="0 8px 32px rgba(0,0,0,0.12)"
              border="1px solid"
              borderColor="whiteAlpha.500"
            >
              <Flex justify="center" py={8}>
                <VStack spacing={6}>
                  <Box
                    p={4}
                    borderRadius="full"
                    bgGradient="linear(135deg, blue.400, purple.500)"
                    w="80px"
                    h="80px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="0 8px 32px rgba(59, 130, 246, 0.3)"
                  >
                    <Spinner size="xl" color="white" />
                  </Box>
                  <VStack spacing={3}>
                    <Text 
                      fontSize="xl" 
                      fontWeight="700"
                      bgGradient="linear(135deg, gray.800, gray.600)"
                      bgClip="text"
                    >
                      Searching Assets
                    </Text>
                    <Text color="gray.600" fontWeight="500">Please wait while we find your assets...</Text>
                  </VStack>
                </VStack>
              </Flex>
            </Box>
          )}

          {!loading && hasSearched && assets.length === 0 && (
            <Box
              bg="rgba(255, 255, 255, 0.8)"
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              p={12}
              boxShadow="0 8px 32px rgba(0,0,0,0.12)"
              border="1px solid"
              borderColor="whiteAlpha.500"
            >
              <VStack spacing={6}>
                <Box
                  p={4}
                  borderRadius="full"
                  bgGradient="linear(135deg, orange.400, orange.600)"
                  w="80px"
                  h="80px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 8px 32px rgba(251, 146, 60, 0.3)"
                >
                  <Text fontSize="3xl">🔍</Text>
                </Box>
                <VStack spacing={3}>
                  <Text 
                    fontSize="xl" 
                    fontWeight="700"
                    bgGradient="linear(135deg, gray.800, gray.600)"
                    bgClip="text"
                  >
                    No Assets Found
                  </Text>
                  <Text color="gray.600" fontWeight="500" textAlign="center" maxW="md">
                    No asset records found for the specified search criteria. Try adjusting your filters.
                  </Text>
                </VStack>
              </VStack>
            </Box>
          )}

          {!loading && assets.length > 0 && (
            <Box
              bg="rgba(255, 255, 255, 0.8)"
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              boxShadow="0 8px 32px rgba(0,0,0,0.12)"
              border="1px solid"
              borderColor="whiteAlpha.500"
              overflow="hidden"
            >
              <Box 
                p={{ base: 6, md: 8 }} 
                pb={{ base: 4, md: 6 }} 
                borderBottom="1px" 
                borderColor="whiteAlpha.300"
                bg="rgba(34, 197, 94, 0.05)"
              >
                <Flex justify="space-between" align="center">
                  <HStack spacing={3}>
                    <Box
                      p={2}
                      borderRadius="lg"
                      bgGradient="linear(135deg, green.400, green.600)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="sm" color="white">✅</Text>
                    </Box>
                    <Text 
                      fontSize="lg" 
                      fontWeight="700"
                      bgGradient="linear(135deg, green.600, green.800)"
                      bgClip="text"
                    >
                      Search Results ({assets.length} records)
                    </Text>
                  </HStack>
                  <IconButton
                    aria-label="Export Search Results as PDF"
                    icon={<AiFillFilePdf />}
                    size="md"
                    variant="ghost"
                    colorScheme="red"
                    borderRadius="xl"
                    onClick={async () => {
                      await exportAssetsToPDF(
                        assets,
                        'Asset Group Report',
                        `Search Results (${assets.length} records)`,
                        'AssetReport',
                        false // don't include status column for search results
                      );
                    }}
                  />
                </Flex>
              </Box>
              <VStack spacing={6} p={{ base: 6, md: 8 }} align="stretch">
                {assets.map(asset => (
                  <AssetCard key={asset.id} asset={asset} />
                ))}
              </VStack>
            </Box>
          )}
        </VStack>
      </Container>
      
      {/* Global Image Modal - Single instance for all asset images */}
      <ImageViewerModal
        isOpen={imageModalOpen}
        onClose={closeImageModal}
        imageUrl={imageModalUrl || ''}
        title={imageModalTitle}
      />
    </Box>
  );
};

export default AssetManagement;